import { useParams, Link, Navigate } from 'react-router-dom'
import { Check, ArrowRight, ArrowLeft } from 'lucide-react'
import Seo from '../components/Seo.jsx'
import { ServiceStructuredData, BreadcrumbStructuredData } from '../components/StructuredData.jsx'
import Container from '../components/Container.jsx'
import ServiceCard from '../components/ServiceCard.jsx'
import CTA from '../components/CTA.jsx'
import { getService, services } from '../data/services.js'

export default function ServiceDetail() {
  const { slug } = useParams()
  const service = getService(slug)

  if (!service) return <Navigate to="/services" replace />

  const Icon = service.icon
  const related = services
    .filter((s) => s.category === service.category && s.slug !== service.slug)
    .slice(0, 3)

  return (
    <>
      <Seo title={service.title} description={service.summary} path={`/services/${service.slug}`} />
      <ServiceStructuredData name={service.title} description={service.summary} path={`/services/${service.slug}`} />
      <BreadcrumbStructuredData
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: service.title, path: `/services/${service.slug}` },
        ]}
      />

      <main className="pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="pointer-events-none absolute inset-0 bg-grid [mask-image:linear-gradient(to_bottom,white,transparent)]" />
          <div className="pointer-events-none absolute inset-0 glow" />
          <Container className="relative py-16">
            <Link to="/services" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
            <div className="mt-6 flex items-start gap-4">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-brand-400/10 text-brand-300 ring-1 ring-brand-400/20">
                <Icon className="h-7 w-7" />
              </span>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-300">{service.category}</span>
                <h1 className="mt-1 text-3xl font-extrabold sm:text-4xl">{service.title}</h1>
              </div>
            </div>
            <p className="mt-5 max-w-2xl text-lg text-slate-400">{service.tagline}</p>
          </Container>
        </section>

        {/* Body */}
        <section className="py-16">
          <Container>
            <div className="grid gap-10 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <h2 className="text-2xl">Overview</h2>
                <p className="mt-4 leading-relaxed text-slate-400">{service.description}</p>

                <h2 className="mt-12 text-2xl">What's included</h2>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                      <span className="text-sm text-slate-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="card sticky top-24 p-6">
                  <h3 className="text-lg">Interested in {service.title}?</h3>
                  <p className="mt-2 text-sm text-slate-400">
                    Tell us about your project and we’ll get back with a scope and quote, usually within one business day.
                  </p>
                  <Link to="/contact" className="btn-primary mt-5 w-full">
                    Request a quote <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/products" className="btn-ghost mt-3 w-full">View our products</Link>
                </div>
              </aside>
            </div>
          </Container>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="pb-16">
            <Container>
              <h2 className="text-2xl">Related services</h2>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((s) => (
                  <ServiceCard key={s.slug} service={s} />
                ))}
              </div>
            </Container>
          </section>
        )}

        <CTA />
      </main>
    </>
  )
}
